import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  Receipt,
  Hammer,
  LockKey,
  CheckCircle,
  FolderSimple,
  Stack,
  PlusCircle,
  Sparkle,
  Tag,
  CreditCard,
  BookmarkSimple,
  Phone,
  EnvelopeSimple,
  MapPin,
  CaretDown,
  ArrowRight,
} from "@phosphor-icons/react";
import { PortalShell } from "@/components/PortalShell";
import {
  portalApi,
  portalMoney,
  portalDate,
  portalAsset,
  itemSlug,
} from "@/lib/portalApi";
import { formatApiErrorDetail } from "@/lib/api";
import { motion } from "framer-motion";
import { PortalEmpty } from "@/components/PortalEmpty";
import { PortalApprovalsCard } from "@/components/PortalApprovalsCard";
import { SavedCutList } from "@/components/portal/SavedCutList";

const CARD =
  "rounded-2xl border border-slate-200/80 bg-white";
const CARD_HOVER =
  "rounded-2xl border border-slate-200/80 bg-white transition-all duration-200 hover:border-slate-300";

// Subtle staggered entrance for dashboard blocks.
const Reveal = ({ i = 0, className = "", children }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.45, delay: Math.min(i, 9) * 0.06, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

const TAB = {
  emerald: "border-l-emerald-500",
  amber: "border-l-amber-500",
  rose: "border-l-rose-500",
  cyan: "border-l-cyan-500",
  slate: "border-l-slate-300",
};
const BADGE = {
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  amber: "bg-amber-50 text-amber-700 ring-amber-200",
  rose: "bg-rose-50 text-rose-700 ring-rose-200",
  cyan: "bg-cyan-50 text-cyan-700 ring-cyan-200",
  slate: "bg-slate-100 text-slate-600 ring-slate-200",
};

const toneOf = (stage = "") => {
  const s = stage.toLowerCase();
  if (["accepted", "complete", "installed", "paid"].some((k) => s.includes(k)))
    return "emerald";
  if (["declined", "void", "cancel", "past due", "overdue"].some((k) => s.includes(k)))
    return "rose";
  if (["template", "fabrication", "install", "partly", "pending", "action"].some((k) => s.includes(k)))
    return "amber";
  if (["sent", "quoted", "approved", "lead"].some((k) => s.includes(k)))
    return "cyan";
  return "slate";
};

const initials = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

const StatusBadge = ({ children }) => {
  const tone = toneOf(children || "");
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ring-1 ${BADGE[tone]}`}
    >
      {children || "—"}
    </span>
  );
};

const StatusListCard = ({ to, testId, stage, code, title, sub, meta, amount }) => {
  const tone = toneOf(stage || "");
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`group flex min-w-0 items-stretch gap-4 border-l-4 ${TAB[tone]} ${CARD_HOVER} p-5`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono-data text-xs text-slate-400">{code}</span>
          <StatusBadge>{stage}</StatusBadge>
        </div>
        <div className="mt-2 truncate text-sm font-semibold text-slate-900">
          {title || "—"}
        </div>
        {sub && <div className="mt-1 truncate text-xs text-slate-500">{sub}</div>}
        {meta && (
          <div className="mt-3 break-words text-[10px] font-bold uppercase tracking-widest text-slate-400 group-hover:text-teal-700">
            {meta}
          </div>
        )}
      </div>
      {amount ? (
        <div className="shrink-0 self-center text-right">
          <div className="font-mono-data text-lg font-bold text-slate-900">
            {amount}
          </div>
        </div>
      ) : (
        <ArrowRight
          size={18}
          weight="bold"
          className="shrink-0 self-center text-slate-300 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-teal-600"
        />
      )}
    </Link>
  );
};

const SectionHead = ({ icon: Icon, title, action, to, testId }) => (
  <div className="flex flex-wrap items-center gap-3">
    <h2 className="flex items-center gap-2 text-base font-semibold tracking-tight text-slate-900 md:text-lg">
      <Icon size={18} weight="bold" className="text-teal-600" />
      {title}
    </h2>
    {action && (
      <Link
        to={to}
        data-testid={testId}
        className="ml-auto text-[11px] font-bold uppercase tracking-widest text-teal-700 transition-colors duration-150 hover:text-teal-900"
      >
        {action}
      </Link>
    )}
  </div>
);

const Ring = ({ pct }) => {
  const r = 44;
  const c = 2 * Math.PI * r;
  const off = c * (1 - Math.max(0, Math.min(100, pct)) / 100);
  const full = pct >= 100;
  return (
    <svg
      width="116"
      height="116"
      viewBox="0 0 116 116"
      data-testid="portal-payment-progress-ring"
      className="shrink-0"
    >
      <circle cx="58" cy="58" r={r} fill="none" stroke="#e2e8f0" strokeWidth="9" />
      <circle
        cx="58"
        cy="58"
        r={r}
        fill="none"
        stroke={full ? "#10b981" : "#0d9488"}
        strokeWidth="9"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={off}
        transform="rotate(-90 58 58)"
        style={{ transition: "stroke-dashoffset .7s cubic-bezier(.4,0,.2,1)" }}
      />
      <text
        x="58"
        y="53"
        textAnchor="middle"
        className="fill-slate-900"
        style={{ fontSize: 22, fontWeight: 800 }}
      >
        {pct}%
      </text>
      <text
        x="58"
        y="72"
        textAnchor="middle"
        className="fill-slate-400"
        style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5 }}
      >
        {"PAID"}
      </text>
    </svg>
  );
};

const BillingCard = ({ invoices }) => {
  const active = invoices.filter((i) => i.status !== "Void");
  if (active.length === 0) return null;
  const billed = active.reduce((n, i) => n + Number(i.amount || 0), 0);
  const paid = active.reduce((n, i) => n + Number(i.paid || 0), 0);
  const balance = active.reduce((n, i) => n + Number(i.balance || 0), 0);
  const pct = billed > 0 ? Math.min(100, Math.round((paid / billed) * 100)) : 0;
  const open = active.find(
    (i) => Number(i.balance || 0) > 0 && i.can_pay_online,
  );
  const settled = balance <= 0.005;

  return (
    <section className={`${CARD} p-6`} data-testid="portal-billing-card">
      <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-center">
        <Ring pct={pct} />
        <div className="min-w-0 flex-1">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-400">
            {settled ? "Account balance" : "Remaining balance"}
          </div>
          <div
            className="font-mono-data mt-1 text-3xl font-extrabold tracking-tight text-slate-900"
            data-testid="portal-billing-balance"
          >
            {portalMoney(balance)}
          </div>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500">
            <span>
              Paid{" "}
              <strong className="font-mono-data text-slate-700">
                {portalMoney(paid)}
              </strong>
            </span>
            <span>
              Invoiced{" "}
              <strong className="font-mono-data text-slate-700">
                {portalMoney(billed)}
              </strong>
            </span>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {settled ? (
              <span
                className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-4 py-2 text-xs font-bold uppercase tracking-widest text-emerald-700 ring-1 ring-emerald-200"
                data-testid="portal-billing-paid-in-full"
              >
                <CheckCircle size={15} weight="fill" /> Paid in full
              </span>
            ) : (
              <Link
                to={open ? `/portal/invoices/${open.id}` : "/portal/invoices"}
                data-testid="portal-make-payment-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[#c39e6d] px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-[#a9855a]"
              >
                <CreditCard size={16} weight="bold" /> Make a payment
              </Link>
            )}
            <Link
              to="/portal/invoices"
              data-testid="portal-billing-view-invoices"
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-600 transition-colors duration-150 hover:bg-slate-50 hover:text-slate-900"
            >
              View invoices
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const QuickLinks = ({ open }) => {
  const links = [
    {
      to: open ? `/portal/invoices/${open.id}` : "/portal/invoices",
      label: "Make a payment",
      icon: CreditCard,
      testId: "portal-quick-link-payment",
    },
    {
      to: "/portal/request",
      label: "Request a quote",
      icon: PlusCircle,
      testId: "portal-quick-link-quote",
    },
    {
      to: "/portal/inventory",
      label: "Hold a slab remnant",
      icon: BookmarkSimple,
      testId: "portal-quick-link-hold",
    },
    {
      to: "/portal/projects",
      label: "View my projects",
      icon: FolderSimple,
      testId: "portal-quick-link-projects",
    },
  ];
  return (
    <section className={`${CARD} p-6`} data-testid="portal-quick-links-card">
      <div className="text-xs font-bold uppercase tracking-widest text-slate-400">
        Quick links
      </div>
      <div className="mt-4 space-y-2">
        {links.map((l) => {
          const Icon = l.icon;
          return (
            <Link
              key={l.testId}
              to={l.to}
              data-testid={l.testId}
              className="group flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 transition-colors duration-150 hover:border-teal-300 hover:bg-teal-50 hover:text-teal-800"
            >
              <Icon size={18} weight="bold" className="text-teal-600" />
              <span className="flex-1">{l.label}</span>
              <ArrowRight
                size={15}
                weight="bold"
                className="text-slate-300 transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-teal-600"
              />
            </Link>
          );
        })}
      </div>
    </section>
  );
};

const ShopCard = ({ rep, shop }) => {
  if (!rep && !shop) return null;
  const person = rep || null;
  const name = person?.name || shop?.name || "";
  const phone = person?.phone || shop?.phone || "";
  return (
    <section className={`${CARD} p-6`} data-testid="portal-rep-contact-card">
      <div className="text-xs font-bold uppercase tracking-widest text-slate-400">
        {person ? "Your project contact" : "Your shop"}
      </div>
      <div className="mt-4 flex items-center gap-3">
        {person?.has_photo ? (
          <img
            src={portalAsset("/rep-photo")}
            alt={name}
            data-testid="portal-rep-photo"
            className="h-12 w-12 shrink-0 rounded-xl object-cover ring-1 ring-slate-200"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : (
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 text-sm font-bold text-white">
            {initials(name)}
          </div>
        )}
        <div className="min-w-0">
          <div
            className="truncate text-sm font-semibold text-slate-900"
            data-testid="portal-rep-name"
          >
            {name}
          </div>
          <div className="truncate text-xs text-slate-500">
            {person ? person.title : "Here to help with your project"}
          </div>
        </div>
      </div>
      <div className="mt-4 space-y-2 text-sm">
        {phone && (
          <a
            href={`tel:${phone}`}
            data-testid="portal-rep-call-btn"
            className="flex items-center gap-3 rounded-lg px-2 py-1.5 text-slate-600 transition-colors duration-150 hover:bg-slate-50 hover:text-teal-800"
          >
            <Phone size={16} weight="bold" className="text-teal-600" />
            <span>{phone}</span>
            {person?.phone && (
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                Direct
              </span>
            )}
          </a>
        )}
        {person?.email ? (
          <a
            href={`mailto:${person.email}`}
            data-testid="portal-rep-email"
            className="flex items-center gap-3 rounded-lg px-2 py-1.5 text-slate-600 transition-colors duration-150 hover:bg-slate-50 hover:text-teal-800"
          >
            <EnvelopeSimple size={16} weight="bold" className="text-teal-600" />
            <span className="truncate">{person.email}</span>
          </a>
        ) : (
          shop?.website && (
            <a
              href={shop.website.startsWith("http") ? shop.website : `https://${shop.website}`}
              target="_blank"
              rel="noreferrer"
              data-testid="portal-rep-website"
              className="flex items-center gap-3 rounded-lg px-2 py-1.5 text-slate-600 transition-colors duration-150 hover:bg-slate-50 hover:text-teal-800"
            >
              <EnvelopeSimple size={16} weight="bold" className="text-teal-600" />
              {shop.website}
            </a>
          )
        )}
        {shop?.address && (
          <div className="flex items-start gap-3 px-2 py-1.5 text-slate-600">
            <MapPin size={16} weight="bold" className="mt-0.5 shrink-0 text-teal-600" />
            <span>{shop.address}</span>
          </div>
        )}
      </div>
    </section>
  );
};

const FAQ_ITEMS = [
  {
    q: "How do I choose my slab?",
    a: "Browse the Inventory tab to see slabs and remnants in our warehouse. Found one you like? Ask us to hold it and we'll set it aside while you decide.",
  },
  {
    q: "What is the installation timeline?",
    a: "Once your quote is accepted we template, fabricate and install — you can follow each stage and its dates on your Jobs page.",
  },
  {
    q: "What are the payment terms?",
    a: "A deposit is due when you accept the quote, and the balance is due around install. You can pay any invoice securely by card right here in the portal.",
  },
  {
    q: "How long is a slab held for me?",
    a: "Holds last a set number of days and we'll email you a reminder before one expires. You can release or re-request a hold any time from the Holds tab.",
  },
];

const FaqCard = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className={`${CARD} p-6`} data-testid="portal-faq-card">
      <div className="text-xs font-bold uppercase tracking-widest text-slate-400">
        Common questions
      </div>
      <div className="mt-3 divide-y divide-slate-100">
        {FAQ_ITEMS.map((item, i) => {
          const isOpen = open === i;
          return (
            <div key={i} data-testid={`portal-faq-item-${i}`}>
              <button
                type="button"
                data-testid={`portal-faq-toggle-${i}`}
                onClick={() => setOpen(isOpen ? -1 : i)}
                className="flex w-full items-center gap-3 py-3 text-left text-sm font-semibold text-slate-800"
              >
                <span className="flex-1">{item.q}</span>
                <CaretDown
                  size={15}
                  weight="bold"
                  className={`shrink-0 text-slate-400 transition-transform duration-200 ${
                    isOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              {isOpen && (
                <p className="pb-3 text-sm leading-relaxed text-slate-500">
                  {item.a}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

const SetPassword = () => {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [done, setDone] = useState(false);

  const save = async () => {
    try {
      await portalApi.post("/set-password", { password: pw });
      setDone(true);
      toast.success("Password saved — you can sign in with it next time");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not save that password",
      );
    }
  };

  if (done) return null;
  return (
    <div className={`${CARD} p-5`} data-testid="portal-set-password">
      <div className="flex flex-wrap items-center gap-3">
        <LockKey size={18} className="text-teal-600" />
        <p className="text-sm text-slate-500">
          Set a password so you can come back without the emailed link.
        </p>
        {!open && (
          <button
            type="button"
            data-testid="portal-set-password-open"
            onClick={() => setOpen(true)}
            className="ml-auto rounded-full border border-slate-200 px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-600 transition-colors duration-150 hover:bg-slate-50 hover:text-slate-900"
          >
            Set a password
          </button>
        )}
      </div>
      {open && (
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <input
            data-testid="portal-new-password"
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="At least 8 characters"
            className="h-12 flex-1 rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-teal-500"
          />
          <button
            type="button"
            data-testid="portal-save-password"
            disabled={pw.length < 8}
            onClick={save}
            className="h-12 rounded-xl bg-[#c39e6d] px-5 text-[11px] font-bold uppercase tracking-widest text-white hover:bg-[#a9855a] disabled:opacity-40"
          >
            Save password
          </button>
        </div>
      )}
    </div>
  );
};

const StockRow = ({ rows, kind, title, note, icon: Icon, tone }) => {
  if (!rows.length) return null;
  const chip =
    tone === "emerald"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : "border-amber-200 bg-amber-50 text-amber-700";
  return (
    <section data-testid={`portal-${kind}-section`}>
      <SectionHead
        icon={Icon}
        title={title}
        action="See all stock →"
        to="/portal/inventory"
        testId={`portal-${kind}-see-all`}
      />
      <p className="mt-1 text-xs text-slate-400">{note}</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {rows.map((s) => (
          <Link
            key={s.id}
            to={`/portal/inventory/${itemSlug(s.kind, s.color, s.id)}`}
            data-testid={`portal-${kind}-card-${s.id}`}
            className={`group min-w-0 overflow-hidden ${CARD_HOVER}`}
          >
            <div className="relative h-36 bg-slate-100">
              {s.thumb || s.photo ? (
                <img
                  src={portalAsset(`/inventory/photo/${s.thumb || s.photo}`)}
                  alt={s.color}
                  className="h-36 w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              ) : (
                <div className="flex h-36 items-center justify-center">
                  <Stack size={22} className="text-slate-300" />
                </div>
              )}
              <span
                className={`absolute left-2 top-2 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${chip}`}
              >
                {title}
              </span>
            </div>
            <div className="p-4">
              <div className="truncate text-sm font-semibold text-slate-900">
                {s.color || "Unnamed"}
              </div>
              <div className="mt-1 text-xs text-slate-500">
                {[s.kind, s.thickness, s.size].filter(Boolean).join(" · ")}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default function PortalHome() {
  const [projects, setProjects] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [stock, setStock] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [me, setMe] = useState(null);
  const { hash } = useLocation();

  // Jump to the saved-cuts section when arriving from the header shortcut.
  useEffect(() => {
    if (hash !== "#saved-cuts") return;
    const t = setTimeout(() => {
      document.getElementById("saved-cuts")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
    return () => clearTimeout(t);
  }, [hash]);

  useEffect(() => {
    Promise.all([
      portalApi.get("/projects"),
      portalApi.get("/quotes"),
      portalApi.get("/jobs"),
      portalApi.get("/me"),
    ])
      .then(([pr, q, j, m]) => {
        setProjects(pr.data);
        setQuotes(q.data);
        setJobs(j.data);
        setMe(m.data);
      })
      .catch(() => {});
    portalApi
      .get("/inventory")
      .then(({ data }) => setStock(data.items || []))
      .catch(() => setStock([]));
    portalApi
      .get("/invoices")
      .then(({ data }) => setInvoices(Array.isArray(data) ? data : []))
      .catch(() => setInvoices([]));
  }, []);

  const arrivals = stock.filter((s) => s.new_arrival).slice(0, 4);
  const sale = stock.filter((s) => s.on_sale).slice(0, 4);
  const today = useMemo(
    () =>
      new Date().toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      }),
    [],
  );
  const openInvoice = invoices.find(
    (i) => i.status !== "Void" && Number(i.balance || 0) > 0 && i.can_pay_online,
  );
  const name = me?.customer?.name || me?.user?.email || "there";
  const hasBilling = invoices.some((i) => i.status !== "Void");

  return (
    <PortalShell>
      {/* Welcome header */}
      <motion.header
        className="flex flex-col gap-4 sm:flex-row sm:items-center"
        data-testid="portal-welcome-header"
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-500 text-lg font-bold text-white">
          {initials(me?.customer?.name || me?.user?.email)}
        </div>
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-400">
            {today}
          </div>
          <h1
            className="mt-1 truncate text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl"
            data-testid="portal-customer-name"
          >
            Hello, {name}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {me?.customer?.company
              ? `${me.customer.company} · `
              : ""}
            Your quotes, drawings, slab layouts and job progress in one place.
          </p>
        </div>
      </motion.header>

      {me?.visibility?.welcome_note && (
        <p
          className={`mt-6 ${CARD} p-4 text-sm text-slate-600`}
          data-testid="portal-welcome"
        >
          {me.visibility.welcome_note}
        </p>
      )}

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-12">
        {/* Left / main column */}
        <div className="min-w-0 space-y-8 lg:col-span-8">
          <Reveal i={0}>
            <PortalApprovalsCard quotes={quotes} />
          </Reveal>

          {hasBilling && (
            <Reveal i={1}>
              <BillingCard invoices={invoices} />
            </Reveal>
          )}

          {arrivals.length > 0 && (
            <Reveal i={2}>
              <StockRow
                rows={arrivals}
                kind="new-arrivals"
                title="New arrivals"
                note="Just landed in the warehouse"
                icon={Sparkle}
                tone="emerald"
              />
            </Reveal>
          )}
          {sale.length > 0 && (
            <Reveal i={3}>
              <StockRow
                rows={sale}
                kind="on-sale"
                title="On sale"
                note="Sharp prices while they last"
                icon={Tag}
                tone="amber"
              />
            </Reveal>
          )}

          <Reveal i={4}>
            <section data-testid="portal-projects-section">
              <SectionHead
                icon={FolderSimple}
                title="Your projects"
                action={projects.length ? "See all →" : null}
                to="/portal/projects"
                testId="portal-projects-see-all"
              />
              <div className="mt-4 grid gap-3 xl:grid-cols-2">
                {projects.length === 0 && (
                  <PortalEmpty
                    testId="portal-no-projects"
                    className="xl:col-span-2"
                    icon={FolderSimple}
                    title="No projects yet"
                    text="Your project appears here the moment we send your first quote — with its drawing, slab layout and schedule."
                    ctaLabel="Request a quote"
                    ctaTo="/portal/request"
                  />
                )}
                {projects.map((pr) => (
                  <StatusListCard
                    key={pr.id}
                    to={`/portal/projects/${pr.id}`}
                    testId={`portal-project-card-${pr.id}`}
                    stage={pr.stage}
                    code={pr.code}
                    title={pr.name}
                    sub={pr.job_address}
                    meta={`${pr.quotes.length} quote${pr.quotes.length === 1 ? "" : "s"} · ${pr.jobs.length} job${pr.jobs.length === 1 ? "" : "s"}${pr.target_install_date ? ` · install ${portalDate(pr.target_install_date)}` : ""}`}
                    amount={pr.accepted_total ? portalMoney(pr.accepted_total) : ""}
                  />
                ))}
              </div>
            </section>
          </Reveal>

          <Reveal i={5}>
            <section data-testid="portal-quotes-section">
              <SectionHead
                icon={Receipt}
                title="Quotes"
                action={quotes.length ? "See all →" : null}
                to="/portal/quotes"
                testId="portal-quotes-see-all"
              />
              <div className="mt-4 grid gap-3 xl:grid-cols-2">
                {quotes.length === 0 && (
                  <PortalEmpty
                    testId="portal-no-quotes"
                    className="xl:col-span-2"
                    icon={Receipt}
                    title="No quotes yet"
                    text="As soon as we price your project, your quote lands here so you can review the drawing and accept online."
                    ctaLabel="Request a quote"
                    ctaTo="/portal/request"
                  />
                )}
                {quotes.map((q) => (
                  <StatusListCard
                    key={q.id}
                    to={`/portal/quotes/${q.id}`}
                    testId={`portal-quote-card-${q.id}`}
                    stage={
                      (q.pending_actions || []).length
                        ? "Action needed"
                        : q.accepted_at
                          ? "Accepted"
                          : q.status || "Sent"
                    }
                    code={q.code}
                    title={q.project_name}
                    sub={[q.slab_color, q.sqft ? `${q.sqft} ft²` : "", q.job_address]
                      .filter(Boolean)
                      .join(" · ")}
                    meta={`${portalDate(q.created_at)} · View quote`}
                    amount={
                      q.total === null || q.total === undefined
                        ? ""
                        : portalMoney(q.total)
                    }
                  />
                ))}
              </div>
            </section>
          </Reveal>

          <Reveal i={6}>
            <section data-testid="portal-jobs-section">
              <SectionHead
                icon={Hammer}
                title="Jobs"
                action={jobs.length ? "See all →" : null}
                to="/portal/jobs"
                testId="portal-jobs-see-all"
              />
              <div className="mt-4 grid gap-3 xl:grid-cols-2">
                {jobs.length === 0 && (
                  <PortalEmpty
                    testId="portal-no-jobs"
                    className="xl:col-span-2"
                    icon={Hammer}
                    title="No jobs in the shop yet"
                    text="Once you accept a quote we template, fabricate and install — and you'll follow every stage right here."
                    ctaLabel="See my quotes"
                    ctaTo="/portal/quotes"
                    secondaryLabel="Browse inventory"
                    secondaryTo="/portal/inventory"
                  />
                )}
                {jobs.map((j) => (
                  <StatusListCard
                    key={j.id}
                    to={`/portal/jobs/${j.id}`}
                    testId={`portal-job-card-${j.id}`}
                    stage={j.stage}
                    code={j.code}
                    title={j.project_name}
                    sub={j.address}
                    meta={
                      j.install_date
                        ? `Install ${portalDate(j.install_date)}`
                        : "Follow this project"
                    }
                  />
                ))}
              </div>
            </section>
          </Reveal>

          <Reveal i={7}>
            <section id="saved-cuts" data-testid="portal-saved-cuts-section" className="scroll-mt-24">
              <SectionHead
                icon={BookmarkSimple}
                title="Saved cuts"
                action="Browse inventory →"
                to="/portal/inventory"
                testId="portal-saved-cuts-browse"
              />
              <p className="mt-1 text-xs text-slate-400">
                Pieces you sized on a slab — quote one any time.
              </p>
              <div className="mt-4">
                <SavedCutList />
              </div>
            </section>
          </Reveal>
        </div>

        {/* Right sidebar */}
        <aside className="min-w-0 space-y-6 lg:col-span-4">
          <Reveal i={2}>
            <QuickLinks open={openInvoice} />
          </Reveal>
          <Reveal i={3}>
            <ShopCard rep={me?.rep} shop={me?.shop} />
          </Reveal>
          <Reveal i={4}>
            <FaqCard />
          </Reveal>
          {me && !me.has_password && (
            <Reveal i={5}>
              <SetPassword />
            </Reveal>
          )}
        </aside>
      </div>
    </PortalShell>
  );
}
